import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import SideMenu from '../components/SideMenu';
import { Package, FileCheck, Calendar, PiggyBank, Users, TrendingUp, Menu, Eye, MessageCircle, ClipboardList, Bell, Shield, ChevronRight } from 'lucide-react';
import { generateWhatsAppMessage, openWhatsApp } from '../utils/messageTemplates';
import { encryptId } from '../utils/urlEncryption';

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [remindersDueToday, setRemindersDueToday] = useState([]);
  const [showReminderBanner, setShowReminderBanner] = useState(false);
  
  // Use effective_role for access control (supports custom roles with full permissions)
  const effectiveRole = user?.effective_role || user?.role;

  useEffect(() => {
    loadStats();
    loadRemindersDueToday();
  }, []);

  const loadStats = async () => {
    try {
      const res = await axiosInstance.get('/dashboard/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRemindersDueToday = async () => {
    try {
      const res = await axiosInstance.get('/reminders', {
        params: { due_today: true, status: 'open' }
      });
      if (res.data && res.data.length > 0) {
        setRemindersDueToday(res.data);
        setShowReminderBanner(true);
      }
    } catch (err) {
      console.error('Error loading reminders:', err);
    }
  };

  const QuickAccessCard = ({ icon: Icon, label, description, color, badge, onClick, testId }) => (
    <button
      onClick={onClick}
      className="premium-card p-4 text-left hover:shadow-xl transition-all touch-target w-full group"
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-sm text-gray-800 group-hover:text-amber-700 transition-colors">{label}</h3>
            {badge > 0 && (
              <span className="px-1.5 py-0.5 text-xs font-bold text-white rounded-full" style={{ backgroundColor: color, minWidth: '20px', textAlign: 'center' }}>
                {badge}
              </span>
            )}
          </div>
          {description && <p className="text-xs text-gray-500 mt-0.5 truncate">{description}</p>}
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-amber-600 flex-shrink-0 transition-colors" />
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="page-container">
        <div className="page-header">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMenuOpen(true)}
              className="p-2 hover:bg-amber-100 rounded-xl transition-colors"
              data-testid="open-menu-button"
            >
              <Menu className="w-6 h-6 text-gray-700" />
            </button>
            <div>
              <h1 className="page-title" data-testid="dashboard-title">Dashboard</h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Welcome, {user?.full_name || user?.name || 'Guest'}</p>
            </div>
          </div>
        </div>

        {/* Payment Reminders Banner - stays on top */}
        {showReminderBanner && remindersDueToday.length > 0 && (
          <div className="bg-gradient-to-r from-amber-100 to-orange-100 border-2 border-amber-300 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 shadow-lg">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-lg sm:text-xl">💰</span>
                <h3 className="text-sm sm:text-lg font-bold text-gray-800">Payment Reminders Due Today ({remindersDueToday.length})</h3>
              </div>
              <button
                onClick={() => setShowReminderBanner(false)}
                className="p-1.5 hover:bg-white rounded-full transition-colors flex-shrink-0"
              >
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-2">
              {remindersDueToday.slice(0, 3).map(reminder => {
                const sourceId = reminder.order_id || reminder.approval_id || reminder.booking_id;
                const sourceType = reminder.order_id ? 'orders' : reminder.approval_id ? 'approvals' : 'bookings';
                return (
                  <div key={reminder.reminder_id} className="bg-white rounded-lg p-3" data-testid={`reminder-card-${reminder.reminder_id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className="font-semibold text-sm text-gray-800">#{sourceId}</span>
                          <span className="text-xs text-gray-500">Due {new Date(reminder.due_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}</span>
                        </div>
                        {reminder.amount_due > 0 && (
                          <div className="text-sm text-gray-700 mt-0.5">
                            Amount: <span className="font-bold text-amber-700">₹{parseFloat(reminder.amount_due).toLocaleString('en-IN')}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const message = generateWhatsAppMessage(sourceId, 'Customer', reminder.amount_due, reminder.due_at);
                            openWhatsApp(reminder.customer_phone || '', message);
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-semibold hover:bg-green-600 transition-colors"
                          data-testid={`reminder-whatsapp-${reminder.reminder_id}`}
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          WhatsApp
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/${sourceType}/${encryptId(sourceId)}`);
                          }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 transition-colors"
                          data-testid={`reminder-view-${reminder.reminder_id}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {remindersDueToday.length > 3 && (
                <div className="text-center">
                  <button
                    onClick={() => navigate('/payment-reminders')}
                    className="text-sm text-amber-700 font-semibold hover:text-amber-800"
                  >
                    View all {remindersDueToday.length} reminders →
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-amber-600 mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-5 fade-in">

            {/* Today's Summary - moved to top */}
            {(effectiveRole === 'Admin' || effectiveRole === 'Manager' || effectiveRole === 'Controller') && (
              <div className="premium-card p-4">
                <h2 className="text-base font-bold mb-3" style={{ color: '#333' }}>Today's Summary</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="text-center p-3 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600">{stats.orders_today || 0}</p>
                    <p className="text-xs text-gray-600 mt-1">Orders</p>
                  </div>
                  <div className="text-center p-3 bg-gradient-to-br from-violet-50 to-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-violet-600">{stats.bookings_today || 0}</p>
                    <p className="text-xs text-gray-600 mt-1">Bookings</p>
                  </div>
                  <div className="text-center p-3 bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">{stats.approvals_today || 0}</p>
                    <p className="text-xs text-gray-600 mt-1">Approvals</p>
                  </div>
                  <div className="text-center p-3 bg-gradient-to-br from-pink-50 to-rose-50 rounded-lg">
                    <p className="text-2xl font-bold text-pink-600">{stats.kitty_today || 0}</p>
                    <p className="text-xs text-gray-600 mt-1">Kitty</p>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Access */}
            {(effectiveRole === 'Admin' || effectiveRole === 'Controller') && (
              <div>
                <h2 className="text-base font-bold mb-3" style={{ color: '#333' }}>Quick Access</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <QuickAccessCard
                    icon={Package}
                    label="Order Reports"
                    description="View and manage all orders"
                    color="#d4af37"
                    onClick={() => navigate('/orders/reports')}
                    testId="quick-access-order-reports"
                  />
                  <QuickAccessCard
                    icon={FileCheck}
                    label="Pending Reviews"
                    description="Review staff submissions"
                    color="#f59e0b"
                    badge={stats.pending_reviews || 0}
                    onClick={() => navigate('/admin/approvals')}
                    testId="quick-access-pending-reviews"
                  />
                  <QuickAccessCard
                    icon={Bell}
                    label="Payment Reminders"
                    description="Manage payment reminders"
                    color="#ef4444"
                    onClick={() => navigate('/payment-reminders')}
                    testId="quick-access-payment-reminders"
                  />
                  <QuickAccessCard
                    icon={Shield}
                    label="Report Access Control"
                    description="Grant/revoke staff report access"
                    color="#7c3aed"
                    onClick={() => {
                      window.location.hash = '#/admin/settings?tab=report-access';
                      window.location.reload();
                    }}
                    testId="quick-access-report-access"
                  />
                </div>
              </div>
            )}

            {/* Staff view */}
            {user.role === 'Staff' && (
              <div>
                <h2 className="text-base font-bold mb-3" style={{ color: '#333' }}>Quick Access</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <QuickAccessCard
                    icon={Package}
                    label="My Submissions"
                    description={`${stats.my_submissions || 0} total`}
                    color="#d4af37"
                    onClick={() => navigate('/submissions/my-all')}
                    testId="quick-access-my-submissions"
                  />
                </div>
              </div>
            )}

          </div>
        )}
      </div>

      <SideMenu user={user} isOpen={menuOpen} onClose={() => setMenuOpen(false)} onLogout={onLogout} />
    </div>
  );
};

export default Dashboard;
